import {
  useQuery,
  type DataTag,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import { fetcher } from '../../../../utils/api/fetcher/fetcher';
import type { ApiError } from '../../../../utils/api/ApiError/ApiError';
import {
  sourceSystemShape,
  userRoleShape,
} from '../../../../utils/api/types/access-api-types';

const meShape = z.object({
  userId: z.string(),
  givenName: z.string(),
  familyName: z.string(),
  sourceSystemStatus: z.string().optional(),
  userSource: sourceSystemShape,
  otherSource: z.string().optional(),
  email: z.string().optional(),
  currentRole: userRoleShape,
  currentOrg: z.object({
    orgId: z.string(),
    orgName: z.string(),
    orgRoles: z.array(userRoleShape),
  }),
  userOrgDetails: z.array(
    z.object({
      orgId: z.string(),
      orgName: z.string().nullish(),
      orgRoles: z.array(userRoleShape),
    })
  ),
  userName: z.string().optional(),
  grade: z.string().optional(),
});

export const sessionShape = z
  .object({
    sessionId: z.string(),
    extendSessionUrl: z.string(),
    sessionType: z.string(),
    creationTime: z.string(),
    latestActivityTime: z.string(),
  })
  .optional();

export const impersonationDataShape = z.object({
  impersonationInfo: z
    .object({
      impersonatingUserId: z.string(),
      impersonatingUserName: z.string(),
    })
    .optional(),
  isImpersonated: z.boolean().optional(),
});

export const meResponseShape = z.object({
  userContext: meShape,
  sessionContext: sessionShape,
  ...impersonationDataShape.shape,
});

export type MeResponse = z.infer<typeof meResponseShape>;
export type Me = z.infer<typeof meShape>;

export async function getMeApiWithSessionContext(): Promise<MeResponse> {
  const user = await fetcher<MeResponse>('/me', {
    service: '/auth',
    version: '/v2',
  });

  return meResponseShape.parse(user);
}

const getMeApiQueryKey = () => ['/v2', 'me', 'with-session-context'];

export function meQueryOptions() {
  return {
    queryKey: getMeApiQueryKey() as unknown as DataTag<QueryKey, MeResponse>,
    queryFn: getMeApiWithSessionContext,
    staleTime: Infinity,
  };
}

export function useGetMeApiWithSessionContext(
  customQueryOptions?: Omit<
    UseQueryOptions<MeResponse, ApiError>,
    'queryKey' | 'queryFn' | 'select' | 'staleTime'
  >
): UseQueryResult<MeResponse, ApiError> {
  return useQuery({
    ...customQueryOptions,
    ...meQueryOptions(),
  });
}

export function useGetMeApi(
  customQueryOptions?: Omit<
    UseQueryOptions<MeResponse, ApiError, Me>,
    'queryKey' | 'queryFn' | 'select' | 'staleTime'
  >
): UseQueryResult<Me, ApiError> {
  return useQuery({
    ...customQueryOptions,
    ...meQueryOptions(),
    select: (response: MeResponse): Me => response.userContext,
  });
}

export { sourceSystemShape, userRoleShape };
